import { Injectable, InjectionToken, computed, effect, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  PRICING_CONFIG,
  fallbackSeatAmounts,
  type AmountConfig, type PlanConfig, type PricingGroupConfig,
} from '../data/pricing.config';
import { localePath } from '../utils/locale-path';
import { CatalogRatesService, type LivePlan } from './catalog-rates.service';
import { LanguageService } from './language.service';
import { RegionService } from './region.service';
import type {
  BillingTerm,
  Plan,
  PlanLineItem,
  PriceAmount,
  PricingContent,
  PricingGroup,
  PricingTrial,
} from '../models/pricing.model';
import type { ProductSlug } from '../models/product.model';

/**
 * A thin space groups thousands, on every language and both products:
 * "2 399", never "2,399" and never an unspaced "2399".
 *
 * Not U+202F, the narrow no-break space, which neither shipped typeface
 * carries. IBM Plex Mono has no U+2009 either, so `--av-mono` takes that one
 * character from Archivo (see `fonts.scss`). The amounts take `white-space:
 * nowrap`, so a price still cannot break across the separator.
 */
const GROUP_SEPARATOR = '\u2009';

function withThousandsSeparator(value: string): string {
  const [whole, fraction] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);

  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

function withSymbol(value: string, currency: string): string {
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';

  return symbol ? `${symbol}${value}` : `${value} ${currency}`;
}

/**
 * Where pricing comes from.
 *
 * One source builds every price on the site by joining three things: the shape
 * in `pricing.config.ts` (which plans exist, what each one charges for), the
 * copy in the active translation file, and — for Catalog — the live amounts
 * from `CatalogRatesService`.
 *
 * The live figures are an overlay, never a dependency. Every amount also exists
 * in the config, so the page renders complete before the network answers and
 * stays complete if it never does.
 */
export interface PricingSource {
  /** Recomputed when the language changes, and when live rates arrive. */
  content(): PricingContent;
}

export const PRICING_SOURCE = new InjectionToken<PricingSource>('PRICING_SOURCE', {
  providedIn: 'root',
  factory: () => new JoinedPricingSource(),
});

@Injectable({ providedIn: 'root' })
export class JoinedPricingSource implements PricingSource {
  private readonly translate = inject(TranslateService);
  private readonly language = inject(LanguageService);
  private readonly rates = inject(CatalogRatesService);
  private readonly region = inject(RegionService);

  /** Products with a live price source, by product slug. Core has none until `core-api` exists. */
  private readonly liveRates: Partial<Record<ProductSlug, () => ReadonlyMap<number, LivePlan> | null>> = {
    catalog: () => this.rates.plans(),
  };

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params) as string;
  }

  private tree<T>(key: string): T {
    return this.translate.instant(key) as unknown as T;
  }

  /** The live plan behind a config plan, if this product publishes prices. */
  private live(group: PricingGroupConfig, plan: PlanConfig): LivePlan | null {
    if (plan.userCap == null) return null;

    return this.liveRates[group.productSlug]?.()?.get(plan.userCap) ?? null;
  }

  private amount(config: AmountConfig, live?: { amount: number; currency: string }): PriceAmount {
    return {
      amount: live?.amount ?? config.amount,
      currency: live?.currency ?? this.region.currency(),
      suffix: this.t(`pricing.units.${config.unit}`),
    };
  }

  private terms(
    config: Record<BillingTerm, AmountConfig>,
    live?: LivePlan | null,
  ): Record<BillingTerm, PriceAmount> {
    return {
      monthly: this.amount(config.monthly, live?.prices?.monthly),
      commit12: this.amount(config.commit12, live?.prices?.commit12),
    };
  }

  /**
   * Plan limits are written into the copy — "Up to 1,000 Catalog users" — so
   * they have to move when the live limits do, or the site states a number the
   * product will not honour. The copy carries `{{cap}}` and the number is
   * formatted for the reader's language.
   */
  private cap(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';

    return withThousandsSeparator(String(value));
  }

  /** The largest published limit, for the plan that sits above all of them. */
  private largestCap(group: PricingGroupConfig): number | null {
    if (group.seats) return group.seats.max;

    const caps = group.plans
      .map((plan) => this.live(group, plan)?.userCap ?? plan.userCap ?? null)
      .filter((cap): cap is number => typeof cap === 'number');

    return caps.length ? Math.max(...caps) : null;
  }

  private commitSavingPercent(price: Record<BillingTerm, PriceAmount> | undefined): number {
    const monthly = price?.monthly.amount;
    const commit = price?.commit12.amount;
    if (typeof monthly !== 'number' || typeof commit !== 'number' || monthly <= 0) return 0;

    // The epsilon keeps an exact 20% from flooring to 19 on binary rounding error.
    return Math.floor(((monthly - commit) / monthly) * 100 + 1e-9);
  }

  /** Floored, so "up to N%" never claims more than the best amount in the group delivers. */
  private commitDiscountLabel(plans: readonly Plan[]): string | undefined {
    const prices = plans.flatMap((plan) => [
      plan.price,
      ...(plan.lines ?? []).map((line) => line.price),
    ]);
    const percent = Math.max(0, ...prices.map((price) => this.commitSavingPercent(price)));

    return percent > 0 ? this.t('pricing.commitDiscountLabel', { percent }) : undefined;
  }

  private trial(group: PricingGroupConfig, base: string): PricingTrial | undefined {
    if (!group.trialDays) return undefined;

    const params = { days: group.trialDays };

    // Each note is fetched by index rather than as a tree: a tree lookup hands
    // back the raw strings, and these carry `{{days}}`.
    const notes = this.tree<string[]>(`${base}.trial.notes`) ?? [];

    return {
      days: group.trialDays,
      badge: this.t('common.trialBadge', params),
      title: this.t(`${base}.trial.title`, params),
      notes: notes.map((_, i) => this.t(`${base}.trial.notes.${i}`, params)),
    };
  }

  content(): PricingContent {
    this.language.lang();

    const groups: PricingGroup[] = PRICING_CONFIG.map((group) => {
      const base = `pricing.groups.${group.key}`;
      const largest = this.largestCap(group);

      const plans: Plan[] = group.plans.map((plan) => {
        const p = `${base}.plans.${plan.code}`;
        const copy = this.tree<Record<string, unknown>>(p);
        const live = this.live(group, plan);

        // The plan above the largest limit describes itself by that limit, so
        // it reads from the biggest plan's cap rather than one of its own.
        const userCap = live?.userCap ?? plan.userCap ?? null;
        const params = { cap: this.cap(plan.contactOnly ? largest : userCap) };

        const lines: PlanLineItem[] | undefined = plan.lines?.map((line) => ({
          label: this.t(`${p}.lines.${line.key}.label`),
          note: this.t(`${p}.lines.${line.key}.note`),
          price: this.terms(line.price),
        }));

        return {
          code: plan.code,
          name: this.t(`${p}.name`, params),
          blurb: copy['blurb'] as string,
          limitLabel: copy['limitLabel'] === undefined ? undefined : this.t(`${p}.limitLabel`, params),
          userCap,
          price: plan.price ? this.terms(plan.price, live) : undefined,
          lines,
          features: (copy['features'] as string[]) ?? [],
          featuresNote: copy['featuresNote'] as string | undefined,
          cta: {
            label: copy['cta'] as string,
            href: localePath(plan.cta.href, this.language.lang()),
            external: plan.cta.external,
          },
          featured: plan.featured,
          contactOnly: plan.contactOnly,
        };
      });

      return {
        key: group.key,
        productSlug: group.productSlug,
        productName: this.t(`${base}.productName`),
        accent: group.accent,
        shape: group.shape,
        headline: this.t(`${base}.headline`),
        summary: this.t(`${base}.summary`),
        modelNotes: this.tree<string[]>(`${base}.modelNotes`),
        plans,
        terms: this.tree<string[]>(`${base}.terms`),
        trial: this.trial(group, base),
        commitDiscountLabel: this.commitDiscountLabel(plans),
        seats: group.seats && { min: group.seats.min, max: group.seats.max, step: group.seats.step },
      };
    });

    return { groups };
  }
}

@Injectable({ providedIn: 'root' })
export class PricingService {
  private readonly source = inject(PRICING_SOURCE);
  private readonly rates = inject(CatalogRatesService);
  private readonly region = inject(RegionService);
  private readonly translate = inject(TranslateService);
  private readonly _term = signal<BillingTerm>('monthly');

  readonly content = computed(() => this.source.content());
  readonly groups = computed(() => this.content().groups);
  readonly term = this._term.asReadonly();

  /** The live market's currency once it answers, and the visitor's region's until then. */
  readonly currency = computed(() => this.rates.market()?.currency ?? this.region.currency());

  constructor() {
    // Held until detection settles so the first fetch already carries the
    // region: fetching before it resolves returns the default market, and an
    // EU visitor would watch the grid flip to USD and back.
    effect(() => {
      if (!this.region.settled()) return;

      this.rates.load(this.region.query());
    });
  }

  /** Live rates for a seat count win; the config formula fills in until they arrive. */
  seatPrice(seats: number): Record<BillingTerm, PriceAmount> | null {
    const group = PRICING_CONFIG.find((g) => g.productSlug === 'catalog');
    if (!group?.seats) return null;

    const live = this.rates.plans()?.get(seats);
    if (this.rates.plans() && !live) return null;

    const fallback = fallbackSeatAmounts(seats, group.seats);
    const suffix = this.translate.instant('pricing.units.month') as string;
    const price = (term: BillingTerm): PriceAmount => ({
      amount: live?.prices[term]?.amount ?? fallback[term],
      currency: live?.prices[term]?.currency ?? this.currency(),
      suffix,
    });

    return { monthly: price('monthly'), commit12: price('commit12') };
  }

  setTerm(term: BillingTerm): void {
    this._term.set(term);
  }

  toggleTerm(): void {
    this._term.update((t) => (t === 'monthly' ? 'commit12' : 'monthly'));
  }

  /**
   * Formats an amount for display. Returns null for contact-only prices so the
   * template can render its own wording rather than a fake number.
   */
  format(price: PriceAmount | undefined): string | null {
    if (!price || price.amount === null) return null;
    const value = Number.isInteger(price.amount)
      ? price.amount.toString()
      : price.amount.toFixed(2).replace(/\.00$/, '');

    return withSymbol(withThousandsSeparator(value), price.currency);
  }

  /**
   * The plan's price spread over its user limit — informative only, and only
   * where the plan is priced per organization. A per-licence plan already
   * states a price per user, so deriving a second one there would contradict
   * it, and a plan with no limit has nothing to divide by.
   *
   * Lives here rather than on a page because two surfaces show it — the
   * product's pricing page and the home page's preview of it — and a second
   * copy of the arithmetic is a second thing to keep in step.
   */
  perUser(group: PricingGroup, plan: Plan, term: BillingTerm = this.term()): string | null {
    if (group.shape !== 'org-limit') return null;

    const cap = plan.userCap;
    const price = plan.price?.[term];
    if (!cap || cap <= 0 || !price || price.amount === null) return null;

    return this.formatUnit({ ...price, amount: price.amount / cap });
  }

  /**
   * The list every plan in the group carries, when they genuinely all carry the
   * same one. Catalog's plans differ only by user limit, so repeating it on each
   * card says nothing; Core's tiers differ, and keep their own lists.
   */
  sharedFeatures(group: PricingGroup): readonly string[] | null {
    const [first, ...rest] = group.plans;
    if (!first?.features.length || !rest.length) return null;
    const same = rest.every(
      (plan) =>
        plan.features.length === first.features.length &&
        plan.features.every((feature, i) => feature === first.features[i]),
    );
    return same ? first.features : null;
  }

  formatCount(value: number): string {
    return withThousandsSeparator(Math.round(value).toString());
  }

  /**
   * A derived per-unit figure, always to the cent. Unlike a plan price it is
   * never a round number by design, and the cards only line up if every one of
   * them shows two decimals.
   */
  formatUnit(price: PriceAmount | undefined): string | null {
    if (!price || price.amount === null) return null;

    return withSymbol(withThousandsSeparator(price.amount.toFixed(2)), price.currency);
  }
}
